import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Star,
    MessageSquarePlus,
    Loader2,
    Send,
    Quote,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { name: "", role: "", rating: 0, message: "", website: "" };

function StarRating({ value, onChange, size = "md" }) {
    const [hover, setHover] = useState(0);
    const sizeCls = size === "lg" ? "w-8 h-8" : "w-5 h-5";
    return (
        <div
            className="flex items-center gap-1"
            data-testid="review-rating-input"
        >
            {[1, 2, 3, 4, 5].map((v) => {
                const active = (hover || value) >= v;
                return (
                    <button
                        type="button"
                        key={v}
                        onClick={() => onChange(v)}
                        onMouseEnter={() => setHover(v)}
                        onMouseLeave={() => setHover(0)}
                        data-testid={`review-star-${v}`}
                        className="transition-transform hover:scale-110"
                    >
                        <Star
                            className={`${sizeCls} ${
                                active
                                    ? "fill-signal text-signal"
                                    : "text-slate-300"
                            }`}
                            strokeWidth={1.5}
                        />
                    </button>
                );
            })}
        </div>
    );
}

function ReviewCard({ r }) {
    const date = new Date(r.created_at).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
    return (
        <div
            data-testid={`review-card-${r.id}`}
            className="bg-white border border-slate-200 p-6 sm:p-8 flex flex-col group hover:bg-ink hover:text-white transition-colors duration-500"
        >
            <div className="flex items-center justify-between mb-4">
                <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                            key={i}
                            className={`w-4 h-4 ${
                                i < r.rating
                                    ? "fill-signal text-signal"
                                    : "text-slate-200 group-hover:text-white/20"
                            }`}
                            strokeWidth={1.5}
                        />
                    ))}
                </div>
                <Quote
                    className="w-6 h-6 text-slate-200 group-hover:text-signal transition-colors"
                    strokeWidth={1.5}
                />
            </div>
            <p className="text-base leading-relaxed flex-1 mb-6">
                "{r.message}"
            </p>
            <div className="border-t border-slate-100 group-hover:border-white/10 pt-4 transition-colors">
                <div className="font-display font-bold text-base">
                    {r.name}
                </div>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 group-hover:text-white/50 mt-1 transition-colors">
                    {r.role || "Utilisateur"} · {date}
                </div>
            </div>
        </div>
    );
}

export default function Testimonials() {
    const [reviews, setReviews] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(emptyForm);

    const load = async () => {
        try {
            const { data } = await axios.get(`${API}/reviews`);
            setReviews(data);
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || form.rating < 1 || form.message.length < 10) {
            toast.error(
                "Merci de renseigner votre nom, une note, et un message d'au moins 10 caractères.",
            );
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/reviews`, form);
            toast.success(
                "Merci pour votre avis ! Il sera publié après validation.",
            );
            setForm(emptyForm);
            setOpen(false);
            await load();
        } catch (err) {
            console.error(err);
            const detail = err?.response?.data?.detail;
            toast.error(
                typeof detail === "string"
                    ? detail
                    : "Impossible d'envoyer l'avis. Réessayez plus tard.",
            );
        } finally {
            setLoading(false);
        }
    };

    const avg =
        reviews.length > 0
            ? (
                  reviews.reduce((s, r) => s + r.rating, 0) / reviews.length
              ).toFixed(1)
            : null;

    return (
        <section
            id="testimonials"
            data-testid="testimonials"
            className="section-y bg-[#F8F9FA]"
        >
            <div className="container-x">
                <div className="grid lg:grid-cols-12 gap-10 mb-16 items-end">
                    <div className="lg:col-span-7">
                        <p className="text-xs uppercase tracking-[0.2em] text-signal font-bold mb-4">
                            — Témoignages
                        </p>
                        <h2 className="font-display font-black tracking-tighter text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Vos retours
                            <br />
                            font Carvaelis.
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        {avg && (
                            <div className="flex items-center gap-2 mb-2">
                                <div className="flex gap-0.5">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star
                                            key={i}
                                            className={`w-5 h-5 ${
                                                i < Math.round(avg)
                                                    ? "fill-signal text-signal"
                                                    : "text-slate-300"
                                            }`}
                                        />
                                    ))}
                                </div>
                                <span className="font-display font-bold text-lg text-ink">
                                    {avg}/5
                                </span>
                            </div>
                        )}
                        <p className="text-sm text-slate-500 uppercase tracking-wider">
                            {reviews.length > 0
                                ? `${reviews.length} avis publié${
                                      reviews.length > 1 ? "s" : ""
                                  }`
                                : "Soyez le premier à donner votre avis"}
                        </p>
                    </div>
                </div>

                {reviews.length === 0 ? (
                    <EmptyState onOpen={() => setOpen(true)} />
                ) : (
                    <>
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {reviews.map((r) => (
                                <ReviewCard key={r.id} r={r} />
                            ))}
                        </div>
                        <div className="mt-10 flex justify-center">
                            <button
                                type="button"
                                data-testid="open-review-dialog"
                                onClick={() => setOpen(true)}
                                className="group inline-flex items-center gap-3 bg-ink hover:bg-signal text-white px-8 py-4 font-bold uppercase tracking-wider text-sm transition-colors"
                            >
                                <MessageSquarePlus className="w-4 h-4" />
                                Donner mon avis
                            </button>
                        </div>
                    </>
                )}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    data-testid="review-dialog"
                    className="rounded-none max-w-lg border-2 border-ink p-0"
                >
                    <div className="bg-ink text-white p-6">
                        <DialogHeader>
                            <DialogTitle className="font-display font-black text-2xl tracking-tight text-white">
                                Partagez votre expérience
                            </DialogTitle>
                            <DialogDescription className="text-white/60 text-sm">
                                Votre avis apparaîtra sur cette page.
                                Restez authentique — pas de faux avis.
                            </DialogDescription>
                        </DialogHeader>
                    </div>

                    <form onSubmit={submit} className="p-6 space-y-5">
                        <div className="space-y-2">
                            <Label
                                htmlFor="r-name"
                                className="text-xs uppercase tracking-wider font-bold text-ink"
                            >
                                Nom / Prénom *
                            </Label>
                            <Input
                                id="r-name"
                                data-testid="review-input-name"
                                value={form.name}
                                onChange={(e) =>
                                    setForm({ ...form, name: e.target.value })
                                }
                                placeholder="Jean D."
                                className="rounded-none border-slate-300 focus-visible:ring-signal focus-visible:ring-offset-0 focus-visible:border-signal h-11"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-bold text-ink">
                                Vous êtes
                            </Label>
                            <Select
                                value={form.role}
                                onValueChange={(v) =>
                                    setForm({ ...form, role: v })
                                }
                            >
                                <SelectTrigger
                                    data-testid="review-select-role"
                                    className="rounded-none border-slate-300 focus:ring-signal focus:ring-offset-0 h-11"
                                >
                                    <SelectValue placeholder="Sélectionnez" />
                                </SelectTrigger>
                                <SelectContent className="rounded-none">
                                    <SelectItem value="Client">
                                        Client
                                    </SelectItem>
                                    <SelectItem value="Convoyeur">
                                        Convoyeur
                                    </SelectItem>
                                    <SelectItem value="Professionnel">
                                        Professionnel
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-bold text-ink">
                                Votre note *
                            </Label>
                            <StarRating
                                size="lg"
                                value={form.rating}
                                onChange={(v) =>
                                    setForm({ ...form, rating: v })
                                }
                            />
                        </div>

                        <div className="space-y-2">
                            <Label
                                htmlFor="r-msg"
                                className="text-xs uppercase tracking-wider font-bold text-ink"
                            >
                                Votre avis *
                            </Label>
                            <Textarea
                                id="r-msg"
                                data-testid="review-input-message"
                                value={form.message}
                                rows={4}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        message: e.target.value,
                                    })
                                }
                                placeholder="Racontez votre expérience avec Carvaelis..."
                                className="rounded-none border-slate-300 focus-visible:ring-signal focus-visible:ring-offset-0 focus-visible:border-signal"
                            />
                        </div>

                        {/* Honeypot: invisible field for bots */}
                        <div
                            style={{
                                position: "absolute",
                                left: "-10000px",
                                top: "auto",
                                width: "1px",
                                height: "1px",
                                overflow: "hidden",
                            }}
                            aria-hidden="true"
                        >
                            <label>
                                Ne pas remplir
                                <input
                                    type="text"
                                    name="website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={form.website}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            website: e.target.value,
                                        })
                                    }
                                />
                            </label>
                        </div>

                        <DialogFooter className="pt-2 gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="px-6 py-3 text-sm font-bold uppercase tracking-wider border-2 border-slate-200 text-slate-500 hover:border-ink hover:text-ink transition-colors"
                            >
                                Annuler
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="review-submit"
                                className="inline-flex items-center gap-2 bg-signal hover:bg-signal-dark disabled:opacity-60 text-white px-6 py-3 text-sm font-bold uppercase tracking-wider transition-colors"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Envoi
                                    </>
                                ) : (
                                    <>
                                        Publier
                                        <Send className="w-4 h-4" />
                                    </>
                                )}
                            </button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </section>
    );
}

function EmptyState({ onOpen }) {
    return (
        <div
            data-testid="testimonials-empty"
            className="relative border border-slate-200 bg-white overflow-hidden"
        >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(31,111,240,0.08),transparent_60%)] pointer-events-none" />
            <div className="relative grid lg:grid-cols-12 gap-10 p-10 sm:p-14 lg:p-20 items-center">
                <div className="lg:col-span-7">
                    <div className="flex items-center gap-2 mb-6">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                                key={i}
                                className="w-5 h-5 text-slate-300"
                                strokeWidth={1.5}
                            />
                        ))}
                        <span className="ml-2 text-xs uppercase tracking-wider text-slate-500">
                            En attente des premiers avis
                        </span>
                    </div>
                    <h3 className="font-display font-bold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                        Et si le prochain
                        <br />
                        témoignage
                        <br />
                        <span className="italic font-medium text-signal">
                            c'était le vôtre&nbsp;?
                        </span>
                    </h3>
                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl">
                        Nous préférons attendre vos vrais retours plutôt que
                        de publier de faux avis. Partagez votre expérience
                        en quelques clics.
                    </p>
                </div>
                <div className="lg:col-span-4 lg:col-start-9">
                    <div className="bg-ink p-8 sm:p-10 text-white">
                        <MessageSquarePlus
                            className="w-9 h-9 text-signal mb-6"
                            strokeWidth={1.5}
                        />
                        <h4 className="font-display font-bold text-2xl mb-4 leading-tight">
                            Donnez votre avis
                        </h4>
                        <p className="text-sm text-white/70 leading-relaxed mb-8">
                            Un formulaire simple, votre avis publié
                            directement sur cette page.
                        </p>
                        <button
                            type="button"
                            onClick={onOpen}
                            data-testid="open-review-dialog-empty"
                            className="group inline-flex items-center justify-between w-full gap-3 bg-signal hover:bg-white hover:text-ink text-white px-5 py-4 font-bold uppercase tracking-wider text-xs transition-colors"
                        >
                            Laisser un avis
                            <MessageSquarePlus className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
